"use client";

import { useState, useEffect } from "react";
import Image from "next/image";
import Link from "next/link";
import { navigationData } from "./data";
import { Icons, Images } from "@assets/assets";
import DesktopPopup from "@/components/DesktopPopup";
import dynamic from "next/dynamic";
import AiGradientBorderButton from "@/components/AiGradientBorderButton";

// PERF: the 900-line quote form renders nothing while closed (DesktopPopup
// returns null), yet a static import shipped + hydrated it on every page.
// Load it ssr:false and mount only after the quote popup is first opened;
// an idle-time prefetch keeps the first open instant.
const GetAQuoteForm = dynamic(() => import("@/app/get-a-quote"), {
  ssr: false,
});
import { useTheme } from "@/HomeFeatures/Context/ThemeContext";
import { useNavigationMenu } from "./NavigationMenuContext";
import PrimaryBtn from "@/components/PrimaryBtn";
import GlobalContactButton from "@/components/GlobalContactButton";

type Props = {
  open: boolean;
  // eslint-disable-next-line no-unused-vars
  setOpen: (_open: boolean) => void;
  dark?: boolean;
};

export default function ResponsiveNav({ open, setOpen, dark = true }: Props) {
  const { setMegamenu, setActiveColumn } = useNavigationMenu();
  const { isQuoteOpen, handleOpenQuote, handleCloseQuote } = useTheme();
  const [quoteEverOpened, setQuoteEverOpened] = useState(false);

  // Mount the quote form on first open; keep it mounted afterwards so the
  // close animation and entered form state survive re-opens.
  useEffect(() => {
    if (isQuoteOpen) setQuoteEverOpened(true);
  }, [isQuoteOpen]);

  // Prefetch the form chunk once the browser is idle so the first open
  // doesn't wait on a network request.
  useEffect(() => {
    const prefetch = () => {
      import("@/app/get-a-quote");
    };
    if (typeof window.requestIdleCallback === "function") {
      const id = window.requestIdleCallback(prefetch);
      return () => window.cancelIdleCallback(id);
    }
    const id = window.setTimeout(prefetch, 2500);
    return () => window.clearTimeout(id);
  }, []);

  const handleMainClick = (index: number) => {
    setMegamenu(index);
    setActiveColumn(null);
    setOpen(true);
    handleCloseQuote();
  };

  const handleTogglePopups = () => {
    if (open || isQuoteOpen) {
      setOpen(false);
      handleCloseQuote();
    } else {
      setOpen(true);
      handleCloseQuote();
    }
  };

  return (
    <>
      <div className="flex flex-col max-w-[2500px] w-[calc(100%_-_5%)] z-50 mx-auto py-6 relative navigation-1">
        <nav className="flex items-center justify-between w-full">
          <Link href="/" aria-label="Go to homepage">
            <Image
              src={dark ? Images?.logo : Images?.logoBlue}
              alt="Logo"
              width={150}
              height={30}
              priority={true}
              style={{ aspectRatio: "5/1" }}
              fetchPriority="high"
              className="w-[var(--logo-width)] h-auto"
            />
          </Link>

          <div
            className={`hidden min-[1400px]:flex min-[1300px]:space-x-[25px] min-[1300px]:px-[10px] min-[1300px]:py-[5px] min-[1300px]:rounded-[60px] min-[1300px]:outline-1 
          min-[1300px]:outline-offset-[-1px] justify-start items-center 
          ${
            dark
              ? "min-[1300px]:bg-white/5 min-[1300px]:outline-white/10"
              : "min-[1300px]:bg-white min-[1300px]:outline-[#ECF0F6]"
          }`}
          >
            {/* Original First AI Button */}
            <div className="min-[1280px]:inline-flex hidden m-auto">
              <Link
                href="/ai-development-company"
                aria-label="Contact us - AI Development"
              >
                <AiGradientBorderButton
                  dark={dark}
                  className={`${dark ? "text-white" : "text-black"} cursor-pointer`}
                />
              </Link>
            </div>

            {navigationData?.map((item, index) => {
              const isLastItem = index === navigationData.length - 1;
              const isMegaMenu = item?.megaMenu?.length > 0;
              // If it's the last item (Resources), replace it with the AI button
              if (isLastItem) {
                return (
                  <div key="duplicate-ai-btn" className="inline-flex m-auto">
                    <GlobalContactButton
                      dark={dark}
                      className={`${dark ? "text-white" : "text-black"}`}
                    />
                  </div>
                );
              }

              if (isMegaMenu) {
                return (
                  <button
                    key={index}
                    aria-label={item.label}
                    className={`${
                      dark
                        ? "text-white hover:text-gray-400"
                        : "text-black hover:text-gray-700"
                    } hover:[&_img]:rotate-180 hover:[&_img]:opacity-0.5 p-[var(--primarybtn)] text-[length:var(--primarybtn)] flex gap-1 items-center cursor-pointer mr-0`}
                    onClick={() => handleMainClick(index)}
                  >
                    {item.label}

                    <Image
                      src={dark ? Icons?.chevronDown : Icons?.chevronDownDark}
                      alt="arrow"
                      width={16}
                      height={16}
                      sizes="(max-width: 1024px) 100vw, 50vw"
                      className="w-[var(--dropdown-arrow-nav)] h-[var(--dropdown-arrow-nav)] transition-all duration-500"
                    />
                  </button>
                );
              } else {
                return (
                  <Link
                    key={index}
                    href={item?.link}
                    aria-label={item.label}
                    className={`${
                      dark
                        ? "text-white hover:text-gray-400"
                        : "text-black hover:text-gray-700"
                    } p-[var(--primarybtn)] text-[length:var(--primarybtn)] flex gap-1 items-center cursor-pointer mr-0`}
                  >
                    {item.label}
                  </Link>
                );
              }
            })}
          </div>

          {/* Quote Form Popup */}
          <DesktopPopup active={isQuoteOpen} variant="quote">
            <div className="absolute inset-0 bg-black/40 pointer-events-none" />
            <div className="relative z-[1] w-full h-full">
              {quoteEverOpened && (
                <GetAQuoteForm
                  onClose={() => {
                    handleCloseQuote();
                    setOpen(false);
                  }}
                />
              )}
            </div>
          </DesktopPopup>

          <div className="button-container relative z-[2] flex gap-2">
            <div className="min-[1840px]:inline-flex hidden">
              <PrimaryBtn
                text="Request a Quote"
                iconSrc={dark ? Icons?.chevronBlue : Icons?.chevron}
                onClick={() => {
                  handleOpenQuote();
                }}
                dark={dark}
              />
            </div>

            <button
              className={`h-[var(--primarybtn-height)] w-[var(--primarybtn-height)] 
               rounded-full flex items-center justify-center cursor-pointer border
               transition-all duration-500 [&_*]:transition-all [&_*]:duration-500
               ${
                 open || isQuoteOpen
                   ? "bg-[var(--primary-color)] border-transparent"
                   : dark
                     ? "bg-white/5 border-[rgba(255,255,255,0.2)]"
                     : "bg-white border-[#ECF0F6]"
               }
             `}
              aria-label={open || isQuoteOpen ? "Close Menu" : "Open Menu"}
              onClick={handleTogglePopups}
            >
              <span
                className={`text-2xl leading-none relative w-full  h-full ${
                  open || isQuoteOpen ? "rotate-180" : ""
                }`}
              >
                <span
                  className={`line h-[2px] origin-center ${
                    open || isQuoteOpen
                      ? "rotate-45 top-[50%]"
                      : "top-[calc(50%_-5px)]"
                  } w-[45%] ${
                    dark || open || isQuoteOpen ? "bg-white" : "bg-black"
                  } inline-block left-[50%] absolute transform translate-x-[-50%] translate-y-[-50%]`}
                ></span>
                <span
                  className={`line h-[2px] origin-center ${
                    open || isQuoteOpen
                      ? "rotate-[-45deg] top-[50%]"
                      : "top-[calc(50%_+5px)]"
                  } w-[45%] ${
                    dark || open || isQuoteOpen ? "bg-white" : "bg-black"
                  } inline-block left-[50%] absolute transform translate-x-[-50%] translate-y-[-50%]`}
                ></span>
              </span>
            </button>
          </div>
        </nav>
      </div>
    </>
  );
}
